/* eslint-disable no-unused-vars */
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  assginCreditLine,
  getCreditLineAssignations,
  getProductSubProducts,
} from 'lib/api';
import { getCreditLineFactoringSelectedDecisionEngines } from 'lib/api/decisionEngine';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatDate, formatMoney } from 'lib/functions/utils';
import { showAlert } from 'lib/functions/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import SubPrductAssingnation from './components/Subproducts';

// Type Definitions
interface AssignationModuleProps {
  setOpenShoppingCart: (open: boolean) => void;
  openRequest: any;
  updateStatus: boolean;
  setInAssignation: (inAssignation: boolean) => void;
  setUp: (id: string) => void;
  handleDetails: (id: string) => void;
}

// Component Definitions
const AssignationModule = (props: AssignationModuleProps) => {
  const {
    setOpenShoppingCart,
    openRequest,
    updateStatus,
    setInAssignation,
    setUp,
    handleDetails,
  } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [canUseDecisionEngine, setCanUseDecisionEngine] =
    useState<boolean>(false);
  const [runDecisionEngineClient, setRunDecisionEngineClient] =
    useState<boolean>(false);
  const [confirmationDialog, setConfirmationDialog] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(openRequest.amount);
  const [validUntil, setValidUntil] = useState<Date | null>(
    openRequest.validUntil,
  );
  const daysTillExpiration = useMemo(() => {
    return validUntil
      ? (new Date(validUntil).getTime() - new Date().getTime()) /
          (1000 * 3600 * 24)
      : 0;
  }, [validUntil]);
  const [days, setDays] = useState<number>(Math.floor(daysTillExpiration));
  const [subProducts, setSubProducts] = useState<any[]>([]);
  const [subProductConcentrations, setSubProductConcentrations] = useState<
    any[]
  >([]);

  const handleCloseMainDialog = useCallback(() => {
    setOpenShoppingCart(false);
    handleDetails(openRequest.id);
  }, [openRequest.id]);

  const handleCloseConfirmationDialog = useCallback(() => {
    setConfirmationDialog(false);
  }, []);

  const handleChangeAmount = (value: any) => {
    const cleanValue = value.split('.').join('').split('$').join('');
    if (isNaN(cleanValue)) return;
    if (cleanValue < 0) return;
    setAmount(Number(cleanValue));
  };

  const handleChangeDays = (value: any) => {
    if (value < 0) return;
    setDays(parseInt(value));
    const date = new Date();
    date.setDate(date.getDate() + parseInt(value));
    setValidUntil(date);
  };

  const validateSubProductsConcentrationsAmount = () => {
    if (subProductConcentrations.length === 0) {
      alert('Debes seleccionar al menos un subproducto');
      return false;
    }
    const hasValidAmounts = subProductConcentrations.every(
      (x: any) => x.amount <= amount,
    );
    if (!hasValidAmounts) {
      alert(
        'El monto de las concentraciones de los subproductos no puede ser mayor al total de la línea.',
      );
      return false;
    }
    return true;
  };

  const validateAmount = () => {
    if (amount <= 0) {
      alert('El monto del total de la línea debe ser mayor a 0');
      return false;
    }
    return true;
  };

  const validateOperationCosts = () => {
    for (const concentration of subProductConcentrations) {
      if (
        !concentration.operationCostFixed ||
        concentration.operationCostFixed <= 0
      ) {
        alert(
          'Debes ingresar un costo de operación fijo para todos los subproductos',
        );
        return false;
      }
      if (
        !concentration.operationCostVariable ||
        concentration.operationCostVariable <= 0
      ) {
        alert(
          'Debes ingresar un costo de operación variable para todos los subproductos',
        );
        return false;
      }
    }
    return true;
  };

  const cleanSubProductsConcentrations = () => {
    const newConcentrations = subProductConcentrations.map((x: any) => {
      const newConcentration = {
        subProductId: x.id,
        operationCostVariable: Number(x.operationCostVariable),
        discountRateVariable: Number(x.discountRateVariable),
        operationCostFixed: Number(x.operationCostFixed),
        discountRateFixed: Number(x.discountRateFixed),
        amount: Number(x.amount),
      };
      delete x.id;
      return newConcentration;
    });
    return newConcentrations;
  };

  const getConcentrations = async () => {
    setLoading(true);
    const response = await getCreditLineAssignations(dispatch, {
      id: openRequest.id,
    });
    const { subProductAssignations } = response;
    setSubProductConcentrations(subProductAssignations);
    setLoading(false);
  };

  const setUpBusinessSubProducts = async () => {
    setLoading(true);
    const response = await getProductSubProducts(dispatch, openRequest.id);
    setSubProducts(response);
    setLoading(false);
  };

  const needToSetup = async () => {
    await setUpBusinessSubProducts();
    if (updateStatus) {
      return false;
    } else {
      await getConcentrations();
      setValidUntil(openRequest.validUntil);
    }
  };

  const handlePreAssignation = async () => {
    if (!validateAmount()) return;
    if (!validateSubProductsConcentrationsAmount()) return;
    if (!validateOperationCosts()) return;
    if (!validUntil)
      return alert('Por favor, selecciona una fecha de vencimiento');
    setLoading(true);
    const response =
      await getCreditLineFactoringSelectedDecisionEngines(dispatch);
    response.forEach((engine: any) => {
      if (engine.revisionObjectiveId === 'creditLine') {
        setCanUseDecisionEngine(true);
      }
    });
    setLoading(false);
    setConfirmationDialog(true);
  };

  const handleAssignation = async () => {
    setLoading(true);
    const cleannedSubProductsData = cleanSubProductsConcentrations();
    await assginCreditLine(dispatch, {
      id: openRequest.id,
      amount,
      subProductConcentrations: cleannedSubProductsData,
      validUntil,
      typeId: openRequest.productTypeId,
      runDecisionEngineClient,
      runDecisionEnginePayers: false,
    });
    setInAssignation(false);
    showAlert(dispatch, 'success', 'Se ha asignado la línea con éxito.');
    setOpenShoppingCart(false);
    await setUp(openRequest.id);
    setLoading(false);
    setConfirmationDialog(false);
  };

  useEffect(() => {
    needToSetup();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      <div style={{ padding: '10px 0px' }}>
        <Typography variant="body1">
          Acá debes ingresar el monto de la línea y si tiene concentraciones que
          la limitan
        </Typography>
      </div>

      <div style={{ padding: '10px 0px' }}>
        <Grid container spacing={2} alignItems={'top'}>
          <Grid item xs={6}>
            <Typography fontWeight={600} variant="body1">
              Monto total
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              id="description"
              label="Monto"
              type="text"
              value={formatMoney(amount)}
              onChange={(e) => handleChangeAmount(e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography fontWeight={600} variant="body1">
              Días para el vencimiento
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              id="description"
              label="Días"
              type="number"
              value={days}
              onChange={(e) => handleChangeDays(e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography fontWeight={600} variant="body1">
              Fecha
            </Typography>
            <Typography marginTop={3} fontWeight={600} variant="body1">
              {validUntil ? formatDate(validUntil) : '-'}
            </Typography>
          </Grid>
        </Grid>
      </div>
      <div style={{ padding: '10px 0px', gap: '10px' }}>
        <Typography variant="h3">Asignaciones</Typography>
        <SubPrductAssingnation
          concentrations={subProductConcentrations}
          setConcentrations={setSubProductConcentrations}
          amount={amount}
          subProducts={subProducts}
        />
      </div>
      <DialogActions>
        <CustomButton onClick={handleCloseMainDialog} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton onClick={handlePreAssignation} color="primary">
          Enviar
        </CustomButton>
      </DialogActions>
      <Dialog open={confirmationDialog} onClose={handleCloseConfirmationDialog}>
        <DialogTitle>Nueva asignación</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Estás a punto de asignar una línea de crédito. Si lo necesitas,
            puedes ejecutar el motor de decisión para revisar la asignación de
            la línea.
          </Typography>
          <FormControl>
            <FormControlLabel
              control={<Checkbox />}
              disabled={!canUseDecisionEngine}
              label="Deseo ejecutar el motor de decisión para el cliente"
              onChange={(e: any) =>
                setRunDecisionEngineClient(e.target.checked)
              }
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleCloseConfirmationDialog}
            color="secondary"
          >
            Cancelar
          </CustomButton>
          <CustomButton onClick={handleAssignation} color="primary">
            Enviar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AssignationModule;
